import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PaginationHelper from "../../helper/PaginationHelper";
import {
  createContentCategory,
  createContentSubCategory,
  deleteContentCategory,
  deleteContentSubCategory,
  getContentCategories,
  updateContentCategory,
} from "../../redux/content/contentSlice";
import { getImageUrlHelper, truncateText } from "../../helper/helper";

const ContentLayer = () => {
  const dispatch = useDispatch();
  const { categories } = useSelector((state) => state.content);

  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [categoryTitle, setCategoryTitle] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryFile, setCategoryFile] = useState(null);
  const [editCategoryId, setEditCategoryId] = useState(null);

  const [subTitle, setSubTitle] = useState("");
  const [subDescription, setSubDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");

  useEffect(() => {
    dispatch(getContentCategories());
  }, [dispatch]);

  const rows = useMemo(() => {
    return categories.filter((item) => {
      const text = `${item.title || ""} ${item.description || ""}`.toLowerCase();
      return text.includes(search.toLowerCase());
    });
  }, [categories, search]);

  const totalEntries = rows.length;
  const totalPages = Math.ceil(totalEntries / entries);
  const startIndex = (currentPage - 1) * entries;
  const paginated = rows.slice(startIndex, startIndex + entries);

  const resetCategory = () => {
    setEditCategoryId(null);
    setCategoryTitle("");
    setCategoryDescription("");
    setCategoryFile(null);
  };

  const openEditCategory = (item) => {
    setEditCategoryId(item.id);
    setCategoryTitle(item.title || "");
    setCategoryDescription(item.description || "");
    setCategoryFile(null);
  };

  const resetSubCategory = () => {
    setSubTitle("");
    setSubDescription("");
    setCategoryId("");
  };

  const handleCategorySubmit = () => {
    if (!categoryTitle.trim()) return toast.error("Title is required");
    if (!editCategoryId && !categoryFile) return toast.error("File is required");

    const payload = {
      title: categoryTitle.trim(),
      description: categoryDescription.trim(),
      file: categoryFile,
    };

    const action = editCategoryId
      ? updateContentCategory({ id: editCategoryId, data: payload })
      : createContentCategory(payload);

    dispatch(action)
      .unwrap()
      .then((res) => {
        toast.success(res?.message || (editCategoryId ? "Updated successfully" : "Created successfully"));
        dispatch(getContentCategories());
        resetCategory();
        document.querySelector("#contentCategoryModal .btn-close")?.click();
      })
      .catch((err) => toast.error(err?.message || "Something went wrong"));
  };

  const handleSubCategorySubmit = () => {
    if (!subTitle.trim()) return toast.error("Title is required");
    if (!categoryId) return toast.error("Category is required");

    dispatch(createContentSubCategory({
      title: subTitle.trim(),
      description: subDescription.trim(),
      categoryId,
    }))
      .unwrap()
      .then((res) => {
        toast.success(res?.message || "Created successfully");
        dispatch(getContentCategories());
        resetSubCategory();
        document.querySelector("#contentSubCategoryModal .btn-close")?.click();
      })
      .catch((err) => toast.error(err?.message || "Something went wrong"));
  };

  const handleDeleteCategory = (id) => {
    dispatch(deleteContentCategory(id))
      .unwrap()
      .then(() => toast.success("Deleted successfully"))
      .catch((err) => toast.error(err?.message || "Something went wrong"));
  };

  const handleDeleteSubCategory = (id) => {
    dispatch(deleteContentSubCategory(id))
      .unwrap()
      .then(() => toast.success("Deleted successfully"))
      .catch((err) => toast.error(err?.message || "Something went wrong"));
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between">
          <div className="d-flex gap-3">
            <select className="form-select form-select-sm w-auto" value={entries} onChange={(e) => setEntries(Number(e.target.value))}>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
              <option value="200">200</option>
            </select>
            <input className="form-control w-auto" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-success" data-bs-toggle="modal" data-bs-target="#contentSubCategoryModal" onClick={resetSubCategory}>
              + Add Subcategory
            </button>
            <button className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#contentCategoryModal" onClick={resetCategory}>
              + Add Category
            </button>
          </div>
        </div>

        <div className="card-body p-12">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Subcategories</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((item, i) => {
                  const subCategories = item.subCategories || item.subcategories || [];
                  return (
                    <tr key={item.id}>
                      <td>{startIndex + i + 1}</td>
                      <td>{truncateText(item.title, 28)}</td>
                      <td>{truncateText(item.description, 35)}</td>
                      <td style={{ minWidth: 420, maxWidth: 520 }}>
                        <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 76, overflowY: "auto" }}>
                          {subCategories.map((sub) => (
                            <span key={sub.id} className="badge bg-primary-50 text-primary-600 d-inline-flex align-items-center gap-1">
                              <span title={sub.title}>{truncateText(sub.title, 32)}</span>
                              <button className="text-danger-600 bg-transparent border-0 p-0" onClick={() => handleDeleteSubCategory(sub.id)}>
                                <Icon icon="lucide:x" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle" data-bs-toggle="modal" data-bs-target="#contentCategoryModal" onClick={() => openEditCategory(item)}>
                            <Icon icon="lucide:edit" />
                          </button>
                          <button className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle" onClick={() => handleDeleteCategory(item.id)}>
                            <Icon icon="lucide:trash-2" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationHelper currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={totalEntries} pageSize={entries} />
        </div>
      </div>

      <div className="modal fade" id="contentCategoryModal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5>{editCategoryId ? "Edit" : "Add"} Category</h5>
              <button className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              <label>Title <span className="text-danger">*</span></label>
              <input className="form-control mb-2" value={categoryTitle} onChange={(e) => setCategoryTitle(e.target.value)} placeholder="Enter title" />
              <label>Description</label>
              <textarea className="form-control mb-2" rows={4} value={categoryDescription} onChange={(e) => setCategoryDescription(e.target.value)} placeholder="Enter description" />
              <label>File {!editCategoryId && <span className="text-danger">*</span>}</label>
              <input className="form-control" type="file" onChange={(e) => setCategoryFile(e.target.files?.[0])} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleCategorySubmit}>{editCategoryId ? "Update" : "Save"}</button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="contentSubCategoryModal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5>Add Subcategory</h5>
              <button className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              <label>Category <span className="text-danger">*</span></label>
              <select className="form-select mb-2" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Select category</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>{item.title}</option>
                ))}
              </select>
              <label>Title <span className="text-danger">*</span></label>
              <input className="form-control mb-2" value={subTitle} onChange={(e) => setSubTitle(e.target.value)} placeholder="Enter title" />
              <label>Description</label>
              <textarea className="form-control" rows={4} value={subDescription} onChange={(e) => setSubDescription(e.target.value)} placeholder="Enter description" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleSubCategorySubmit}>Save</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContentLayer;
